import {
  Content,
  Margins,
  Table,
  Size,
  TDocumentDefinitions,
  Style,
  Alignment,
  DynamicRowSize,
  Node,
  PageBreak,
  TableLayout,
  ContentText,
  StyleReference,
  ContentBase,
} from 'pdfmake/interfaces';
import { Primitive } from '@app/shared/types';
import { TCreatedPdf } from 'pdfmake/build/pdfmake';
import { IPdfData, IPdfGridData } from './pdf-data.type';
import { IPdfContentBuilder } from './content-builder/pdf-content-builder.type';
import { PdfContentType } from './pdf-content.enum';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { NumberFormatService } from '@app/shared/services/number-format/number-format.service';
import { CompanyUtilService } from '@app/shared/services/company/company-util.service';
import { UiActions } from '@app/store/actions/ui/ui.actions';
import { StateService } from '@app/shared/services/state-service/state.service';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';

type PdfContent = Content & {
  rowSpan?: number;
  colSpan?: number;
};

type IPdfPageBreakFunc = (
  currentNode: Node,
  followingNodesOnPage: Node[],
  nodesOnNextPage: Node[],
  previousNodesOnPage: Node[],
) => boolean;

interface IPdfTitle<T = {}> {
  text: string | PredicateTitleFn<T>;
  style?: Style;
}

type PredicateTitleFn<T = {}> = (
  params: IPdfGridValueParams<T>,
) => string | string[];

/**
 * @description now only support sub title array build
 */
type PdfTitle = string | PredicateTitleFn | IPdfTitle;

type PdfOptionFields =
  | 'info'
  | 'pageOrientation'
  | 'pageBreakBefore'
  | 'pageMargins'
  | 'pageSize'
  | 'watermark'
  | 'styles';

interface IPdfOptions extends Pick<TDocumentDefinitions, PdfOptionFields> {
  /**
   * use this to set each grid default col width to *
   */
  defaultEqualColumnWidth?: boolean;

  /**
   * column selector
   * u can use this to config shown what column selector before get pdf data
   * column selector will always shown if this is configured
   */
  columnSelector?: IPdfColumnSelector;

  extraConfigurations?: SignatureConfiguration;

  /**
   * use this to store all selected column in column selector
   * will use this to judge what column need to shown
   * caution!!: this will be override by popColumnSelector if columnSelector is configured
   * u can configure this and not configure columnSelector to judge what columns is selected
   */
  selectedColumns?: PdfSelectColumn[];

  isBulkExport?: boolean;

  useSmallerFontInTable?: boolean;

  /**
   * is not need to get server info every time
   */
  isGetInfoEveryTime?: boolean;

  /**
   * is showing Loading when generate pdf
   */
  isShownLoadingWhenGenerate?: boolean;

  sortNumber?: string;

  gridHeaderFontSize?: number;
}

type PdfFiledValue<T = Primitive> = T;

type PdfData = IPdfData | IPdfGridData[];

interface IPdfServices {
  numberFormatService: NumberFormatService;
  companyService: CompanyUtilService;
  uiActions: UiActions;
  stateService: StateService;
  i18nService: XVII18nService;
}

interface IPdfUtils {
  widthOfString: (text: string, options?: PDFKit.Mixins.TextOptions) => number;
}

interface IPdfPreference {
  size: string[];
  width: number;
  height: number;
  margins: Margins;
}

interface IPdfTools {
  /**
   * now will provider this at abstract pdf content builder
   * you can override this value by set this
   */
  services?: IPdfServices;
  utils: IPdfUtils;
  preference: IPdfPreference;
}

interface IPdfValueParams<T = any> {
  value: PdfFiledValue<T>;
  /**
   * root pdf data
   */
  data: IPdfData;

  /**
   * pdf tools, provide some service or common option for user to build pdf
   */
  tools: IPdfTools;

  options?: IPdfOptions;
}

interface IPdfGridValueParams<T = {}> extends Omit<IPdfValueParams<T>, 'data'> {
  /**
   * root pdf data
   */
  data: PdfData;

  /**
   * root gird data
   */
  gridData: IPdfGridData[];

  /**
   * grid row data
   * only set value when use this in grid
   */
  rowData?: IPdfGridData;
  /**
   * grid row index
   */
  rowIndex?: number;

  options?: IPdfOptions;
}

interface IPdfMultiGridParams {
  tools: IPdfTools;
  data: IPdfGridData[];
}

type PdfValueFormatterValue = string | number | boolean;

type ValueGetterFn<T = {}, P = IPdfValueParams<T>> = (
  params: P,
) => PdfFiledValue<T>;

type PredicateHidden<T = IPdfValueParams> = (params: T) => boolean;

type MultiGridFn<T = Primitive> = (
  params: IPdfMultiGridParams,
) => Record<string, PdfFiledValue[]> | PdfFiledValue<T>[] | IPdfData[];

type StyleFn<T = {}> = (params: IPdfGridValueParams<T>) => Style;

type PdfValueFormatterFn<T = {}, P = IPdfValueParams<T>> = (
  params: P,
) => PdfValueFormatterValue;

type PdfFieldBuilder<T = {}> = (
  col: IPdfColumn,
  valueParams: IPdfGridValueParams<T>,
  options: IPdfOptions,
) => PdfContent;

interface IPdfField<T = any> {
  title?: string;

  path?: string;

  width?: '*' | 'auto' | Size;

  height?: number | 'auto' | DynamicRowSize;

  margin?: Margins;

  alignment?: Alignment;

  style?: Style;

  colSpan?: number;

  defaultValue?: PdfFiledValue<T>;

  valueGetter?: ValueGetterFn<T, IPdfValueParams<T>>;
  valueFormatter?: PdfValueFormatterFn<T, IPdfValueParams<T>>;

  /**
   * use this to custom build your filed content
   */
  builder?: PdfFieldBuilder<T>;
}

interface IPdfSummaryField<T = any> extends IPdfField<T> {
  /**
   * is hidden this summary
   */
  hidden?: boolean | PredicateHidden;
}

type PdfSummaryFields<T = any> = Record<string, IPdfSummaryField<T>>;

interface IPdfSimpleSummary<T = any> {
  fieldsPerRow: number;

  fields?: PdfSummaryFields<T>;
  margin?: Margins;
}

interface IPdfArrSummary<T = any> {
  fields?: PdfSummaryFields<T>[];

  margin?: Margins;
}

type PdfSummary<T = any> = IPdfSimpleSummary<T> | IPdfArrSummary<T>;

interface IPdfSimpleText {
  text: string;

  fontSize?: ContentText['fontSize'];
  bold?: ContentText['bold'];
  margin?: Margins;
  style?: StyleReference;
}

type PdfSimpleText = IPdfSimpleText | IPdfSimpleText[];

interface IPdfGridColHeader {
  text?: string;
  style?: Style;
  alignment?: Alignment;
}

interface IPdfColumn<T = any>
  extends Omit<IPdfField<T>, 'style' | 'valueGetter' | 'valueFormatter'> {
  path: string;

  /**
   * is required column
   * will always shown when print pdf
   */
  required?: boolean;

  hiddenColumn?: boolean | PredicateHidden<IPdfGridValueParams<T>>;

  /**
   * column header
   */
  header?: IPdfGridColHeader | string;

  style?: Style | StyleFn;

  valueGetter?: ValueGetterFn<T, IPdfGridValueParams<T>>;
  valueFormatter?: PdfValueFormatterFn<T, IPdfGridValueParams<T>>;

  rowSpan?: number;
  colSpan?: number;
  children?: IPdfColumn[];
}

enum ViewSupportType {
  filter = 'filter',
  sort = 'sort',
  sortAndFilter = 'sortAndFilter',
}

interface IPdfGridView {
  baseOnGrid: XuxEditGridComponent;
  viewSupport?: ViewSupportType;
}

type PredicateGridView = () => IPdfGridView;

type PdfGridView = IPdfGridView | PredicateGridView;

type PredicateOrderBy = () => string[];

/**
 * ex:
 * string[] : ['item_name asc', 'count desc']
 * PredicateOrderBy: () => ['item_name asc', 'count desc']
 * XuxEditGridComponent: will use this component to get grid sort model
 */
type OrderBy = string[] | PredicateOrderBy | XuxEditGridComponent;

type ColumnDefaultStyleProperties =
  | 'alignment'
  | 'height'
  | 'margin'
  | 'width'
  | 'style'
  | 'valueFormatter'
  | 'colSpan'
  | 'valueGetter';

type ColumnDefaultStyle = Pick<IPdfColumn, ColumnDefaultStyleProperties>;

type PredicateColumnDefaultStyle = (
  col: IPdfColumn,
  gridData: IPdfGridData,
  pdfData: IPdfData,
) => ColumnDefaultStyle;

interface IPdfGrid
  extends Pick<Table, 'dontBreakRows'>,
    INestedPdfContentSetting {
  /**
   * grid title
   */
  title?: PdfTitle;

  subTitle?: PdfTitle;

  /**
   * grid field path
   */
  path?: string;

  /**
   * multi header count
   * will use pdf multi header
   */
  headerRows?: number;

  /**
   * grid columns
   * will hidden some columns if the column selector result not contains those columns
   */
  columns: IPdfColumn[];

  /**
   * column default style define, will use this if custom not define custom style itself
   */
  columnDefaultStyle?: ColumnDefaultStyle | PredicateColumnDefaultStyle;

  /**
   * is hidden this grid/table
   */
  hidden?: boolean | PredicateHidden<IPdfGridValueParams>;

  /**
   * is hidden this row
   */
  hiddenRow?: boolean | PredicateHidden<IPdfGridValueParams>;

  /**
   * ex:
   * string[] : ['item_name asc', 'count desc']
   * PredicateOrderBy: () => ['item_name asc', 'count desc']
   * XuxEditGridComponent: will use this component to get grid sort model
   */
  orderBy?: OrderBy;

  /**
   * is case insensitive when order by
   */
  orderByCaseInsensitive?: boolean;

  /**
   * is effected by global selected columns
   */
  // isIgnoreByGlobalColumnSelected?: boolean;

  pageBreak?: PageBreak;

  /**
   * disable page break
   */
  alwaysInOnePage?: boolean;

  rowStyle?: Style | StyleFn;

  /**
   * Layout for the table, or a reference to a predefined or global layout.
   *
   * Defaults to a layout with black borders.
   */
  layout?: TableLayout;

  // model?: GridModel;
  view?: PdfGridView;
  // viewBaseOnGrid?: XuxEditGridComponent;

  /**
   * set this to decide is need split grid data to multi-grid
   */
  multiGrid?: number | MultiGridFn;

  /**
   * set this to decide some column data need to split single row to multi-rows
   */
  multiRow?: {
    /**
     * the target split data field path in the row data
     */
    baseOnRowFieldPath: string;
    /**
     * the special splitter
     * pass it to split & map it if just split it as simple array is not meet your requirement
     */
    splitter?: (
      rowData: IPdfGridData,
      gridData: IPdfGridData[],
      grid: IPdfGrid,
      data: PdfData,
    ) => IPdfData[];
  };

  height?: number;
}

interface IPdfGridOptions extends INestedPdfContentOptions {
  grid: IPdfGrid;
  gridData: IPdfGridData[];
  fieldPath: string;
  contentKey: string;
}

interface IPdfSignature {
  text: string;

  style?: Style;
  fontSize?: number;
  margin?: Margins;
  alignment?: Alignment;
  width?: '*' | 'auto' | Size;
  signatureLineLength?: number;
  signatureSpaceLength?: number;
  bold?: boolean;
  withoutSignatureLine?: boolean;
}

type PdfSignature = string | IPdfSignature;

type PdfMakeFields = 'header' | 'footer';

interface INestedPdfContent {
  /**
   * set this to display some content before this
   * it's will display before at every grids(if multi grid)
   */
  before?: Record<string, IPdfContent>;

  /**
   * set this to display some content after this
   * it's will display after at every grids(if multi grid)
   */
  after?: Record<string, IPdfContent>;
}

/**
 * nested pdf content options
 * pls avoid to pass new fields to buildNestedContent
 * method position is (src/app/shared/common/make-pdf/v2/content-builder/default.pdf-content-builder.ts line 514)
 * if you add new fields
 */
interface INestedPdfContentOptions {
  /**
   * pre grid data list(in the content builder),
   * will assign this value when build split multi grid
   */
  preGridData?: IPdfGridData[];
  /**
   * current grid data list(in the content builder),
   * will assign this value when build split multi grid
   */
  curGridData?: IPdfGridData[];
  /**
   * next grid data list(in the content builder),
   * will assign this value when build split multi grid
   */
  nextGridData?: IPdfGridData[];
  /**
   * split multi gird build index(in the content builder),
   * will assign this value when build split multi grid
   */
  multiGridBuildIndex?: number;

  /**
   * split multi girds total count(in the content builder),
   * will assign this value when build split multi grid
   */
  multiGridCount?: number;
}

type PredicateNestedPdfContent = (
  pdfData: IPdfData,
  options: INestedPdfContentOptions,
) => INestedPdfContent;

type NestedPdfContent = INestedPdfContent | PredicateNestedPdfContent;

interface INestedPdfContentSetting {
  /**
   * nested pdf content
   * you can config some nested pdf content here
   * only working on grids now
   */
  nestedContent?: NestedPdfContent;
}

type PdfContentBase = Pick<ContentBase, 'pageBreak'>;

type PdfContentSetting =
  | PdfSimpleText
  | PdfSummary
  | Record<string, IPdfGrid>
  | PdfSignature[]
  | PdfSignature[][];

type PredicatePdfContentSetting = (
  data: PdfData,
  options?: IPdfOptions,
  tools?: IPdfTools,
) => PdfContentSetting;

type PredicateContentHidden = (data: PdfData, options?: IPdfOptions) => boolean;

interface IPdfContent extends PdfContentBase {
  /**
   * content type
   * will use this to find default content builder
   */
  type: PdfContentType;

  /**
   * content setting
   */
  setting: PdfContentSetting | PredicatePdfContentSetting;

  /**
   * is hidden this content
   */
  hidden?: boolean | PredicateContentHidden;

  /**
   * value path
   */
  path?: string;

  /**
   * is need get child data
   * will just use root data if not set this
   */
  isGetChildData?: boolean;

  /**
   * specific builder
   */
  builder?: IPdfContentBuilder;
}

type PredicateHeaderFn = (
  data: IPdfData,
  tools: IPdfTools,
  options: IPdfOptions,
) => TDocumentDefinitions['header'];

type PredicateFileNameFn = (data: IPdfData, options: IPdfOptions) => string;

type PredicateBulkFileNameFn = (
  pdfDataList: IPdfData[],
  customPdfDefinition?: IPdfDefinitions,
) => string;

type PredicateBulkPdfDataFn = () => Promise<IPdfData[]>;

interface IPdfBulkOptions {
  data: IPdfData[] | PredicateBulkPdfDataFn;

  exportFileName?: string | PredicateBulkFileNameFn;

  batchCount?: number;

  sortFiledPath?: string;
}

interface IPdfDefinitions extends Pick<TDocumentDefinitions, PdfMakeFields> {
  /**
   * pdf file name
   */
  filename?: string;

  generateFileName?: PredicateFileNameFn;

  /**
   * pdf date
   */
  date?: string;

  /**
   * pdf title
   * #TODO confirm with wengpeng
   */
  title?: string;

  /**
   * will use this result to override header value
   */
  generateHeader?: PredicateHeaderFn;

  /**
   * pdf content
   * you can config all your pfd content here
   */
  content?: Record<string, IPdfContent>;

  /**
   * pdf addition options
   */
  options?: IPdfOptions;

  /**
   * pass this means u need bulk print
   */
  bulkOptions?: IPdfBulkOptions;

  data?: IPdfData | PredicatePdfData;
}

interface IPdfDefinitionsWithInnerProperty
  extends Omit<IPdfDefinitions, 'options'> {
  tools: IPdfTools;

  /**
   * pdf inner addition options
   */
  options?: IPdfOptions;
}

type PdfSelectColumn = Partial<Pick<IPdfColumn, 'path' | 'title' | 'required'>>;

type PdfColumnSelectorCacheCallback = (
  selectedColumns: PdfSelectColumn[],
  uiActions: UiActions,
) => PdfSelectColumn[] | void;

type PdfColumnSelectorCloseCallback = (
  selectedColumns: PdfSelectColumn[],
) => PdfSelectColumn[] | void;

type PredicateCachedSelectColumns = () => PdfSelectColumn[];

interface IColumnSelectorEffectCondition {
  type?: 'all' | 'includes' | 'excludes';
  /**
   * grid content paths key or gird field paths
   */
  paths?: string[];
}

interface IPdfSignatureColumn {
  [key: string]: IPdfSignature;
}

type SignatureConfiguration = boolean | IPdfSignatureColumn;

type TotalsDefaultValueGetter = (
  data: IPdfData,
  bulkData: IPdfData[],
) => boolean;

interface TotalsConfiguration {
  enabled: boolean;
  getDefaultValue?: TotalsDefaultValueGetter;
}

interface IPdfColumnSelector {
  columns?: PdfSelectColumn[];

  /**
   * will use all columns in the target grid to popup Column Selector
   * if give string will split by '.' and then use arr[0] as gridPath, and others as settingPath
   * if give array, will use arr[0] as gridPath, and others as settingPath
   * you only need to config gridPath if you need get all setting columns of target gridPath
   */
  columnsFromGridPath?:
    | string
    | string[]
    | {
        gridPath: string;
        settingPath?: string;
      };

  /**
   * will call this when column selector is close with confirm
   * u could custom selected columns here
   * will use the return value of it to be final selected columns
   * if return value is undefined/null, will use passed selected columns as the final selected columns
   */
  onClose?: PdfColumnSelectorCloseCallback;

  /**
   * will call this when column selector is close with confirm
   * u could save custom selected columns here
   * will use the return value of it to save selected columns
   */
  onCacheSelectItems?: PdfColumnSelectorCacheCallback;

  /**
   * will return the selected columns saved before
   * will use the return value of displayed columns selector
   */
  getCacheSelectItems?: PredicateCachedSelectColumns;

  /**
   * will reset the selected columns and cache
   * will use the default value of displayed columns selector
   */
  resetCallback?: PdfColumnSelectorCloseCallback;

  effectCondition?: IColumnSelectorEffectCondition;
  showSelectAll?: boolean;
  showSelectedCount?: boolean;
  showResetButton?: boolean;
  cacheKey?: string;
  supportSelectItemsCache?: boolean;
  twoColumnLayout?: boolean;
  requiredNotDisabled?: boolean;
  tips?: string;
  maxSelectCount?: number;
  signatureConfiguration?: SignatureConfiguration;
  totalsConfiguration?: TotalsConfiguration;
}

/**
 * bulkData will pass when bulk print
 */
type PredicatePdfData = (
  options?: Partial<IPdfDefinitions>,
  bulkData?: IPdfData,
) => Promise<IPdfData>;

interface ICreatedPdf {
  /**
   * our pdf definitions
   * CAUTION!!
   * avoid oom
   */
  pdfData?: IPdfData;

  /**
   * created pdf data
   */
  doc?: TCreatedPdf;

  filename?: string;

  pdfMakeDefinition?: TDocumentDefinitions;
}
interface IColSpanContentText extends ContentText {
  colSpan?: number;
}

export {
  IPdfPageBreakFunc,
  PdfOptionFields,
  IPdfOptions,
  PdfFiledValue,
  PdfData,
  IPdfValueParams,
  IPdfGridValueParams,
  IPdfField,
  IPdfTitle,
  PredicateTitleFn,
  PdfTitle,
  IPdfSummaryField,
  PdfSummaryFields,
  IPdfSimpleSummary,
  IPdfArrSummary,
  PdfSummary,
  IPdfColumn,
  IPdfGridColHeader,
  IPdfGrid,
  IPdfSignature,
  PdfSignature,
  IPdfDefinitions,
  ICreatedPdf,
  PdfValueFormatterFn,
  PdfFieldBuilder,
  PdfContent,
  IPdfColumnSelector,
  PdfGridView,
  IPdfGridView,
  PredicateGridView,
  ViewSupportType,
  OrderBy,
  IPdfServices,
  IPdfUtils,
  IPdfTools,
  IPdfDefinitionsWithInnerProperty,
  PdfContentSetting,
  PdfColumnSelectorCloseCallback,
  PdfSelectColumn,
  IPdfBulkOptions,
  IColumnSelectorEffectCondition,
  IPdfGridOptions,
  IColSpanContentText,
  ColumnDefaultStyle,
  PredicateColumnDefaultStyle,
  PredicatePdfContentSetting,
  IPdfContent,
  PdfSimpleText,
  INestedPdfContentSetting,
  INestedPdfContent,
  NestedPdfContent,
  INestedPdfContentOptions,
  PredicateNestedPdfContent,
  PdfContentBase,
  SignatureConfiguration,
  TotalsConfiguration,
  TotalsDefaultValueGetter,
  IPdfSignatureColumn,
};
